import React, { Suspense, lazy, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Alert, Box, Heading, HStack, Link, Menu, NativeSelect, Spinner, Text, Button, Switch } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useColorModeValue } from '../../components/ui/color-mode';
import { LuChevronDown, LuCheck, LuCopy, LuClipboardCheck, LuExternalLink } from 'react-icons/lu';
import html2canvas from 'html2canvas';
import { CopyChartButton, ChartWatermark } from '../../components/ChartSharing';
import { getStockPriceHistory } from '../../api';
import { useNotableDates } from '../../hooks/useNotableDates';
import { useStockInfo } from '../../hooks/useStockInfo';
import StockPriceHistoryChart from './StockPriceHistoryChart';
import { useStockReviewMode } from './StockReviewModeContext';
import { computeStockPriceChangeSinceDate, isAfterDate } from '../../utils/stockReviewMode';

function ReviewMetricDot({ cx, cy, payload }) {
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;

  const isHighlighted = payload?.isAfterReviewDate === true;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={isHighlighted ? 6 : 3}
      fill={isHighlighted ? '#DD6B20' : '#2F855A'}
      stroke={isHighlighted ? '#FFFFFF' : '#2F855A'}
      strokeWidth={isHighlighted ? 2 : 1}
      data-review-highlight={isHighlighted ? 'true' : 'false'}
    />
  );
}

export function buildCustomMetricChartData(values, sinceDate = null) {
  return (values || []).map((point) => {
    const reviewDate = point.filing_date || point.period_end;
    return {
      date: point.period_end,
      value: point.value,
      isAfterReviewDate: sinceDate ? isAfterDate(reviewDate, sinceDate) : false,
    };
  });
}

const CustomMetricRechartsChart = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
  }) => ({
    default: function CustomMetricRechartsChart({
      metric,
      chartData,
      gridStroke,
      tooltipRenderer,
      reviewSinceDate,
    }) {
      return (
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="date"
              tickFormatter={formatFiscalYear}
              tick={{ fontSize: 12 }}
              minTickGap={20}
            />
            <YAxis
              tickFormatter={(value) => formatCustomMetricValue(value, metric.unit)}
              tick={{ fontSize: 12 }}
              width={90}
              domain={['auto', 'auto']}
            />
            <Tooltip content={tooltipRenderer} />
            <Line
              type="monotone"
              dataKey="value"
              name={metric.display_name}
              stroke="#2F855A"
              strokeWidth={2}
              dot={reviewSinceDate ? ReviewMetricDot : true}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      );
    },
  })),
);

const FinancialRechartsLineChart = lazy(() =>
  import('recharts').then(({
    ResponsiveContainer,
    LineChart,
    Line,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
  }) => ({
    default: function FinancialRechartsLineChart({
      filteredChartData,
      legendPayload,
      combinedMetricOrder,
      activeMetrics,
      lineType,
      gridStroke,
      tooltipRenderer,
      tickFormatter,
      minTickGap,
    }) {
      return (
        <ResponsiveContainer width="100%" height={420}>
          <LineChart
            data={filteredChartData}
            margin={{
              top: 10,
              right: 20,
              left: 10,
              bottom: legendPayload.length ? 60 : 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
            <XAxis
              dataKey="date"
              tickFormatter={tickFormatter}
              tick={{ fontSize: 12 }}
              minTickGap={minTickGap}
            />
            <YAxis
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fontSize: 12 }}
              width={90}
              domain={['auto', 'auto']}
            />
            <Tooltip content={tooltipRenderer} />
            {legendPayload.length > 0 && (
              <Legend
                verticalAlign="bottom"
                align="center"
                layout="horizontal"
                wrapperStyle={{ paddingTop: 16 }}
                payload={legendPayload}
              />
            )}
            {combinedMetricOrder.map((metricKey) => {
              const config = METRIC_CONFIG[metricKey] || {};
              return (
                <Line
                  key={metricKey}
                  type={lineType}
                  dataKey={metricKey}
                  name={config.label || metricKey}
                  stroke={config.color || '#8884d8'}
                  strokeWidth={2}
                  dot={false}
                  hide={activeMetrics[metricKey] === false}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      );
    },
  })),
);

const METRIC_CONFIG = {
  revenue: { label: 'Revenue', color: '#F1C646' },
  netIncome: { label: 'Net Income', color: '#5E6472' },
  operatingCashFlow: { label: 'Operating Cash Flow', color: '#3182CE' },
  longTermDebt: { label: 'Long Term Debt', color: '#E53E3E' },
  stockholdersEquity: { label: 'Stockholders Equity', color: '#FF5B9E' },
  stockBuybacks: { label: 'Stock Buybacks', color: '#00FFFF' },
};

const ORDERED_METRICS = Object.keys(METRIC_CONFIG);
const SMOOTH_LINES_STORAGE_KEY = 'stockChartsSmoothLines';
const CUSTOM_METRICS_BANNER_STORAGE_KEY = 'stockChartsCustomMetricsBannerDismissed';
const CHART_EXPORT_BACKGROUND = '#FFFFFF';
const CHART_EXPORT_WATERMARK_COLOR = 'rgba(15, 23, 42, 0.45)';
const CHART_EXPORT_WATERMARK_SHADOW = '0 0 3px rgba(255, 255, 255, 0.95)';

function getInitialSmoothLines() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const storage = window.localStorage;
    const storedValue = storage.getItem(SMOOTH_LINES_STORAGE_KEY);
    return storedValue === null ? false : storedValue === 'true';
  } catch (err) {
    console.warn('Unable to read smooth lines preference', err);
    return false;
  }
}

function readDismissedBannerTickers() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const storedValue = window.localStorage.getItem(CUSTOM_METRICS_BANNER_STORAGE_KEY);
    if (!storedValue) {
      return {};
    }
    const parsed = JSON.parse(storedValue);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (err) {
    console.warn('Unable to read dismissed custom metrics banners', err);
    return {};
  }
}

function isCustomMetricsBannerDismissed(ticker) {
  if (!ticker) {
    return false;
  }
  return Boolean(readDismissedBannerTickers()[ticker]);
}

function persistCustomMetricsBannerDismissed(ticker) {
  if (typeof window === 'undefined' || !ticker) {
    return;
  }

  try {
    const dismissed = readDismissedBannerTickers();
    dismissed[ticker] = true;
    window.localStorage.setItem(
      CUSTOM_METRICS_BANNER_STORAGE_KEY,
      JSON.stringify(dismissed),
    );
  } catch (err) {
    console.warn('Unable to persist dismissed custom metrics banner', err);
  }
}

function getInitialChartState() {
  return {
    chartData: [],
    metrics: ORDERED_METRICS,
    hiddenMetrics: {},
    loading: true,
    error: null,
    startYear: null,
    endYear: null,
    copyStatus: 'idle',
    smoothLines: getInitialSmoothLines(),
    customMetrics: [],
    customMetricsLoading: false,
    customMetricsError: null,
    selectedDataset: 'stockPrice',
    financialPeriod: 'annual',
    financialView: 'chart',
    selectedCustomMetricKey: '',
    customMetricView: 'chart',
    customMetricsBannerDismissed: false,
    priceHistoryRange: DEFAULT_STOCK_PRICE_HISTORY_RANGE,
    stockPriceHistory: {
      prices: [],
      loading: false,
      error: null,
      priceStatus: null,
      priceMessage: null,
      startDate: null,
      endDate: null,
    },
  };
}

function stockChartsReducer(state, action) {
  switch (action.type) {
    case 'fetch-skipped':
      return { ...state, loading: false };
    case 'fetch-start':
      return { ...state, loading: true, error: null };
    case 'fetch-success':
      return {
        ...state,
        chartData: action.chartData,
        metrics: action.metrics,
        loading: false,
      };
    case 'fetch-error':
      return { ...state, error: action.error, loading: false };
    case 'toggle-metric': {
      if (!state.hiddenMetrics[action.metricKey]) {
        return {
          ...state,
          hiddenMetrics: { ...state.hiddenMetrics, [action.metricKey]: true },
        };
      }

      const hiddenMetrics = { ...state.hiddenMetrics };
      delete hiddenMetrics[action.metricKey];
      return { ...state, hiddenMetrics };
    }
    case 'set-year-range':
      return {
        ...state,
        startYear: Object.prototype.hasOwnProperty.call(action, 'startYear')
          ? action.startYear
          : state.startYear,
        endYear: Object.prototype.hasOwnProperty.call(action, 'endYear')
          ? action.endYear
          : state.endYear,
      };
    case 'set-copy-status':
      return { ...state, copyStatus: action.copyStatus };
    case 'set-smooth-lines':
      return { ...state, smoothLines: action.smoothLines };
    case 'custom-fetch-start':
      return { ...state, customMetricsLoading: true, customMetricsError: null };
    case 'custom-fetch-success': {
      const metrics = action.metrics || [];
      const firstMetricKey = metrics[0]?.key || '';
      const selectedCustomMetricKey = metrics.some(
        (metric) => metric.key === state.selectedCustomMetricKey,
      )
        ? state.selectedCustomMetricKey
        : firstMetricKey;
      return {
        ...state,
        customMetrics: metrics,
        customMetricsLoading: false,
        selectedCustomMetricKey,
        selectedDataset:
          state.selectedDataset === 'custom' && metrics.length === 0
            ? 'financials'
            : state.selectedDataset,
      };
    }
    case 'custom-fetch-error':
      return {
        ...state,
        customMetricsLoading: false,
        customMetricsError: action.error,
      };
    case 'set-selected-dataset':
      return { ...state, selectedDataset: action.selectedDataset };
    case 'set-financial-period':
      if (state.financialPeriod === action.financialPeriod) {
        return state;
      }
      return {
        ...state,
        financialPeriod: action.financialPeriod,
        startYear: null,
        endYear: null,
      };
    case 'set-financial-view':
      return { ...state, financialView: action.financialView };
    case 'set-banner-dismissed':
      return { ...state, customMetricsBannerDismissed: action.dismissed };
    case 'set-selected-custom-metric':
      return { ...state, selectedCustomMetricKey: action.metricKey };
    case 'set-custom-metric-view':
      return { ...state, customMetricView: action.view };
    case 'set-price-history-range':
      return { ...state, priceHistoryRange: action.rangeKey };
    case 'price-fetch-skipped':
      return {
        ...state,
        stockPriceHistory: {
          ...state.stockPriceHistory,
          loading: false,
        },
      };
    case 'price-fetch-start':
      return {
        ...state,
        stockPriceHistory: {
          ...state.stockPriceHistory,
          prices: [],
          loading: true,
          error: null,
          priceStatus: null,
          priceMessage: null,
          startDate: action.startDate,
          endDate: action.endDate,
        },
      };
    case 'price-fetch-success':
      return {
        ...state,
        stockPriceHistory: {
          prices: action.prices || [],
          loading: false,
          error: null,
          priceStatus: action.priceStatus || null,
          priceMessage: action.priceMessage || null,
          startDate: action.startDate,
          endDate: action.endDate,
        },
      };
    case 'price-fetch-error':
      return {
        ...state,
        stockPriceHistory: {
          ...state.stockPriceHistory,
          prices: [],
          loading: false,
          error: action.error,
        },
      };
    default:
      return state;
  }
}

function prepareChartExportClone(documentClone) {
  const exportRoot = documentClone.querySelector('[data-chart-export-root="true"]');
  if (exportRoot) {
    exportRoot.style.backgroundColor = CHART_EXPORT_BACKGROUND;
  }

  const watermark = documentClone.querySelector('[data-chart-watermark="true"]');
  if (watermark) {
    watermark.style.background = 'transparent';
    watermark.style.color = CHART_EXPORT_WATERMARK_COLOR;
    watermark.style.opacity = '1';
    watermark.style.fontWeight = '600';
    watermark.style.textShadow = CHART_EXPORT_WATERMARK_SHADOW;
  }
}

function formatCurrency(value) {
  if (value == null || Number.isNaN(value)) {
    return '—';
  }

  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const format = (num, suffix = '') => {
    const formatted = num >= 10 ? num.toFixed(1) : num.toFixed(2);
    return `${sign}$${parseFloat(formatted).toString()}${suffix}`;
  };

  if (abs >= 1e12) return format(abs / 1e12, 'T');
  if (abs >= 1e9) return format(abs / 1e9, 'B');
  if (abs >= 1e6) return format(abs / 1e6, 'M');
  if (abs >= 1e3) return format(abs / 1e3, 'K');

  return `${sign}$${abs.toLocaleString()}`;
}

function formatFiscalLabel(dateString) {
  if (!dateString) return 'Fiscal Year';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return `Fiscal year ended ${date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })}`;
}

function formatFiscalYear(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return dateString;
  }
  return date.getFullYear();
}

function parseIsoDateUtc(dateString) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString || '');
  if (!match) {
    return null;
  }
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatQuarterTick(dateString) {
  const date = parseIsoDateUtc(dateString);
  if (!date) {
    return dateString || '';
  }
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const year = String(date.getUTCFullYear()).slice(-2);
  return `${month} '${year}`;
}

function formatQuarterLabel(dateString) {
  const date = parseIsoDateUtc(dateString);
  if (!date) {
    return dateString || 'Quarter';
  }
  const formatted = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return `Quarter ended ${formatted}`;
}

const FINANCIAL_PERIOD_COPY = {
  annual: {
    empty: 'We could not find 10-K financial history for this company yet.',
    caption: 'Values represent the most recent annual 10-K filing for each fiscal year.',
  },
  quarterly: {
    empty: 'We could not find quarterly financial history for this company yet.',
    caption: 'Values are standalone quarters from 10-Q and 10-K filings. A quarter reported only inside a year-to-date total is derived by subtracting the prior cumulative amount.',
  },
};

function formatIsoDate(date) {
  return date.toISOString().slice(0, 10);
}

function subtractUtcMonthsClamped(date, months) {
  const originalDay = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() - months);
  const lastDayOfTargetMonth = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    0,
  )).getUTCDate();
  date.setUTCDate(Math.min(originalDay, lastDayOfTargetMonth));
}

const STOCK_PRICE_HISTORY_RANGE_OPTIONS = [
  { value: '1m', label: '1M', amount: 1, unit: 'month' },
  { value: '6m', label: '6M', amount: 6, unit: 'month' },
  { value: 'ytd', label: 'YTD' },
  { value: '1y', label: '1Y', amount: 1, unit: 'year' },
  { value: '5y', label: '5Y', amount: 5, unit: 'year' },
  { value: 'max', label: 'Max' },
];
const DEFAULT_STOCK_PRICE_HISTORY_RANGE = '5y';
const MAX_STOCK_PRICE_HISTORY_START_DATE = '1900-01-01';

export function buildPriceHistoryRange(
  rangeKey = DEFAULT_STOCK_PRICE_HISTORY_RANGE,
  referenceDate = new Date(),
) {
  const endDate = formatIsoDate(referenceDate);
  const rangeOption = STOCK_PRICE_HISTORY_RANGE_OPTIONS.find((option) => option.value === rangeKey)
    || STOCK_PRICE_HISTORY_RANGE_OPTIONS.find((option) => option.value === DEFAULT_STOCK_PRICE_HISTORY_RANGE);

  if (rangeOption?.value === 'max') {
    return {
      startDate: MAX_STOCK_PRICE_HISTORY_START_DATE,
      endDate,
    };
  }

  if (rangeOption?.value === 'ytd') {
    return {
      startDate: `${endDate.slice(0, 4)}-01-01`,
      endDate,
    };
  }

  const startDateValue = new Date(`${endDate}T00:00:00Z`);
  if (Number.isNaN(startDateValue.getTime())) {
    return buildPriceHistoryRange(DEFAULT_STOCK_PRICE_HISTORY_RANGE, referenceDate);
  }

  if (rangeOption?.unit === 'month') {
    subtractUtcMonthsClamped(startDateValue, rangeOption.amount);
  } else {
    startDateValue.setUTCFullYear(startDateValue.getUTCFullYear() - (rangeOption?.amount || 5));
  }

  return {
    startDate: formatIsoDate(startDateValue),
    endDate,
  };
}

async function loadChartData(apiBaseUrl, ticker, signal, period = 'annual') {
  const params = new URLSearchParams({ ticker });
  if (period === 'quarterly') {
    params.set('period', 'quarterly');
  }
  const response = await fetch(
    `${apiBaseUrl}/chart-data?${params.toString()}`,
    { signal },
  );

  if (!response.ok) {
    throw new Error(`Chart data request failed (${response.status})`);
  }

  return response.json();
}

async function loadCustomMetrics(apiBaseUrl, ticker, signal) {
  const response = await fetch(
    `${apiBaseUrl}/custom-metrics?ticker=${encodeURIComponent(ticker)}`,
    { signal },
  );

  if (!response.ok) {
    throw new Error(`Custom metrics request failed (${response.status})`);
  }

  return response.json();
}

function MetricSelector({
  combinedMetricOrder,
  activeMetrics,
  triggerLabel,
  onToggleMetric,
}) {
  const menuBg = useColorModeValue('white', 'gray.800');
  const menuBorder = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Menu.Root closeOnSelect={false}>
      <Menu.Trigger asChild>
        <Button
          size="sm"
          variant="outline"
          cursor="pointer"
          display="inline-flex"
          alignItems="center"
          gap="2"
          maxW="100%"
        >
          <Text truncate>{triggerLabel}</Text>
          <LuChevronDown style={{ flexShrink: 0 }} />
        </Button>
      </Menu.Trigger>
      <Menu.Positioner>
        <Menu.Content
          bg={menuBg}
          borderColor={menuBorder}
          borderWidth="1px"
          borderRadius="md"
          boxShadow="lg"
          minW="280px"
          py={1}
        >
          {combinedMetricOrder.map((metricKey) => {
            const config = METRIC_CONFIG[metricKey] || {};
            const isActive = activeMetrics[metricKey] !== false;
            return (
              <Menu.Item
                key={metricKey}
                value={metricKey}
                onClick={() => onToggleMetric(metricKey)}
                _hover={{ bg: hoverBg, cursor: 'pointer' }}
                px={3}
                py={2}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  w="100%"
                >
                  <Box display="flex" alignItems="center" gap="2">
                    <Box
                      w="10px"
                      h="10px"
                      borderRadius="full"
                      bg={config.color || '#8884d8'}
                      flexShrink={0}
                      border="1px solid"
                      borderColor="blackAlpha.300"
                    />
                    <Text fontSize="sm" color={config.color}>
                      {config.label || metricKey}
                    </Text>
                  </Box>
                  {isActive && <LuCheck size={16} color="#12bc75" />}
                </Box>
              </Menu.Item>
            );
          })}
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  );
}

function YearRangeSelector({
  availableYears,
  effectiveStart,
  effectiveEnd,
  onStartYearChange,
  onEndYearChange,
}) {
  if (availableYears.length <= 1) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap="2">
      <NativeSelect.Root size="sm" w="90px">
        <NativeSelect.Field
          value={effectiveStart}
          onChange={(e) => onStartYearChange(Number(e.target.value))}
        >
          {availableYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      <Text fontSize="sm" color="gray.500">
        to
      </Text>
      <NativeSelect.Root size="sm" w="90px">
        <NativeSelect.Field
          value={effectiveEnd}
          onChange={(e) => onEndYearChange(Number(e.target.value))}
        >
          {availableYears.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
    </Box>
  );
}

function FinancialPeriodSelector({
  financialPeriod,
  onFinancialPeriodChange,
  financialView,
  onFinancialViewChange,
}) {
  const periodOptions = [
    { value: 'annual', label: 'Annual' },
    { value: 'quarterly', label: 'Quarterly' },
  ];
  const viewOptions = [
    { value: 'chart', label: 'Chart' },
    { value: 'table', label: 'Table' },
  ];

  return (
    <Box
      mb="4"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="3"
      flexWrap="wrap"
    >
      <Box display="flex" alignItems="center" gap="3" flexWrap="wrap">
        <Text fontSize="sm" fontWeight="medium" color="gray.500">
          Results
        </Text>
        <HStack gap="1" wrap="wrap" role="group" aria-label="Financial reporting period">
          {periodOptions.map((option) => {
            const isSelected = option.value === financialPeriod;
            return (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={isSelected ? 'solid' : 'outline'}
                colorPalette={isSelected ? 'teal' : 'gray'}
                onClick={() => onFinancialPeriodChange(option.value)}
                aria-pressed={isSelected}
              >
                {option.label}
              </Button>
            );
          })}
        </HStack>
      </Box>
      <HStack gap="2" role="group" aria-label="Results view">
        {viewOptions.map((option) => {
          const isSelected = option.value === financialView;
          return (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={isSelected ? 'solid' : 'outline'}
              colorPalette="blue"
              onClick={() => onFinancialViewChange(option.value)}
              aria-pressed={isSelected}
            >
              {option.label}
            </Button>
          );
        })}
      </HStack>
    </Box>
  );
}

function ChartControls({
  combinedMetricOrder,
  activeMetrics,
  triggerLabel,
  onToggleMetric,
  onCopyChart,
  copyStatus,
  smoothLines,
  onSmoothLinesChange,
  availableYears,
  effectiveStart,
  effectiveEnd,
  onStartYearChange,
  onEndYearChange,
  financialView = 'chart',
}) {
  const controlLabelColor = useColorModeValue('gray.600', 'gray.300');
  const showChartTools = financialView !== 'table';

  return (
    <Box
      mb="4"
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap="3"
    >
      <MetricSelector
        combinedMetricOrder={combinedMetricOrder}
        activeMetrics={activeMetrics}
        triggerLabel={triggerLabel}
        onToggleMetric={onToggleMetric}
      />
      {showChartTools && <CopyChartButton status={copyStatus} onClick={onCopyChart} />}
      {showChartTools && (
        <Box display="flex" alignItems="center" gap="2">
          <Switch.Root
            id="smooth-lines-toggle"
            size="sm"
            checked={smoothLines}
            onCheckedChange={(e) => onSmoothLinesChange(e.checked)}
          >
            <Switch.HiddenInput aria-label="Toggle smooth lines" />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Switch.Label fontSize="sm" color={controlLabelColor}>
              Smooth lines
            </Switch.Label>
          </Switch.Root>
        </Box>
      )}
      <YearRangeSelector
        availableYears={availableYears}
        effectiveStart={effectiveStart}
        effectiveEnd={effectiveEnd}
        onStartYearChange={onStartYearChange}
        onEndYearChange={onEndYearChange}
      />
    </Box>
  );
}

function formatEndedPhrase(dateString, financialPeriod) {
  const date = parseIsoDateUtc(dateString);
  if (!date) {
    return dateString || '';
  }
  const ended = date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
  return financialPeriod === 'quarterly'
    ? `Three months ended ${ended}`
    : `Year ended ${ended}`;
}

function formatTablePeriod(row, financialPeriod) {
  if (row?.period_label) {
    return row.period_label;
  }
  return formatEndedPhrase(row?.date, financialPeriod) || '—';
}

export function selectChartRowSource(row, visibleMetricKeys) {
  const sources = row?.sources || {};
  for (const metricKey of visibleMetricKeys) {
    const value = row?.[metricKey];
    if (value == null || Number.isNaN(Number(value))) {
      continue;
    }
    return sources[metricKey] || null;
  }
  return null;
}

function ChartSourceLink({ source }) {
  if (!source?.form && !source?.url) {
    return '—';
  }
  const label = source.form || 'Filing';
  if (!source.url) {
    return label;
  }
  return (
    <Box
      as="a"
      href={source.url}
      target="_blank"
      rel="noreferrer"
      color="blue.500"
      fontWeight="semibold"
      display="inline-flex"
      alignItems="center"
      gap="1"
      _hover={{ color: 'blue.600' }}
    >
      {label}
      <LuExternalLink size={14} aria-hidden="true" />
    </Box>
  );
}

function FinancialResultsTable({
  filteredChartData,
  combinedMetricOrder,
  activeMetrics,
  financialPeriod,
}) {
  const borderColor = useColorModeValue('var(--chakra-colors-gray-200)', 'var(--chakra-colors-gray-700)');
  const headerBg = useColorModeValue('var(--chakra-colors-gray-50)', 'var(--chakra-colors-gray-800)');
  const visibleMetrics = combinedMetricOrder.filter((metricKey) => activeMetrics[metricKey] !== false);
  const headerCellStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${borderColor}`,
    background: headerBg,
    fontSize: '0.875rem',
    whiteSpace: 'nowrap',
  };
  const cellStyle = {
    padding: '10px 12px',
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: 'nowrap',
  };

  if (!visibleMetrics.length) {
    return (
      <Box textAlign="center" py="10">
        <Text color="gray.500">Select at least one metric to see the table.</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto" data-testid="financial-results-table">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...headerCellStyle, textAlign: 'left' }}>Period</th>
            {visibleMetrics.map((metricKey) => {
              const config = METRIC_CONFIG[metricKey] || {};
              return (
                <th key={metricKey} style={{ ...headerCellStyle, textAlign: 'right' }}>
                  {config.label || metricKey}
                </th>
              );
            })}
            <th style={{ ...headerCellStyle, textAlign: 'left' }}>Source</th>
          </tr>
        </thead>
        <tbody>
          {filteredChartData.map((row) => {
            const source = selectChartRowSource(row, visibleMetrics);
            return (
              <tr key={row.date}>
                <td style={cellStyle} title={formatEndedPhrase(row.date, financialPeriod)}>
                  {formatTablePeriod(row, financialPeriod)}
                </td>
                {visibleMetrics.map((metricKey) => (
                  <td key={metricKey} style={{ ...cellStyle, textAlign: 'right', fontWeight: 600 }}>
                    {formatCurrency(row[metricKey])}
                  </td>
                ))}
                <td style={cellStyle}>
                  <ChartSourceLink source={source} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}

function DatasetSelector({ selectedDataset, hasCustomMetrics, onDatasetChange }) {
  const options = [
    { value: 'stockPrice', label: 'Stock Price' },
    { value: 'financials', label: 'Financials' },
    ...(hasCustomMetrics ? [{ value: 'custom', label: 'Company KPIs' }] : []),
  ];

  return (
    <Box mb="4" display="flex" alignItems="center" gap="3" flexWrap="wrap">
      <Text fontSize="sm" fontWeight="medium" color="gray.500">
        Dataset
      </Text>
      <HStack gap="1" wrap="wrap" role="group" aria-label="Select chart dataset">
        {options.map((option) => {
          const isSelected = option.value === selectedDataset;
          return (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={isSelected ? 'solid' : 'outline'}
              colorPalette={isSelected ? 'teal' : 'gray'}
              onClick={() => onDatasetChange(option.value)}
              aria-pressed={isSelected}
            >
              {option.label}
            </Button>
          );
        })}
      </HStack>
    </Box>
  );
}

function CustomMetricControls({
  customMetrics,
  selectedMetricKey,
  customMetricView,
  onMetricChange,
  onViewChange,
  onCopyTable,
  copyTableStatus,
}) {
  return (
    <Box mb="4" display="flex" alignItems="center" gap="3" flexWrap="wrap">
      <NativeSelect.Root size="sm" w={{ base: 'full', sm: '260px' }}>
        <NativeSelect.Field
          value={selectedMetricKey}
          onChange={(event) => onMetricChange(event.target.value)}
          aria-label="Select company KPI"
        >
          {customMetrics.map((metric) => (
            <option key={metric.key} value={metric.key}>
              {metric.display_name}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      <Box display="flex" gap="2">
        <Button
          size="sm"
          variant={customMetricView === 'chart' ? 'solid' : 'outline'}
          onClick={() => onViewChange('chart')}
        >
          Chart
        </Button>
        <Button
          size="sm"
          variant={customMetricView === 'table' ? 'solid' : 'outline'}
          onClick={() => onViewChange('table')}
        >
          Table
        </Button>
      </Box>
      {customMetricView === 'table' && onCopyTable && (
        <Button
          size="sm"
          variant="outline"
          onClick={onCopyTable}
          display="inline-flex"
          alignItems="center"
          gap="1.5"
          ml="auto"
        >
          {copyTableStatus === 'copied' ? (
            <><LuClipboardCheck size={14} /> Copied!</>
          ) : copyTableStatus === 'error' ? (
            <><LuCopy size={14} /> Copy failed</>
          ) : (
            <><LuCopy size={14} /> Copy</>
          )}
        </Button>
      )}
    </Box>
  );
}

function formatCustomMetricValue(value, unit) {
  if (value == null || Number.isNaN(Number(value))) {
    return '—';
  }
  const numericValue = Number(value);
  if (unit === 'millions') {
    return `${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}M`;
  }
  if (unit === 'billions') {
    return `${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}B`;
  }
  if (unit === 'percent') {
    return `${numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  if (unit === 'dollars') {
    return formatCurrency(numericValue);
  }
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function CustomMetricChart({ metric, chartData, gridStroke, reviewSinceDate }) {
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');

  const tooltipRenderer = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length || payload[0].value == null) return null;
      return (
        <Box
          bg={tooltipBg}
          border="1px solid"
          borderColor={tooltipBorder}
          borderRadius="md"
          boxShadow="lg"
          p="3"
          color={tooltipText}
          display="flex"
          flexDirection="column"
          gap="1"
        >
          <Text fontSize="sm">{formatFiscalLabel(label)}</Text>
          <Text fontWeight="semibold">
            {formatCustomMetricValue(payload[0].value, metric.unit)}
          </Text>
        </Box>
      );
    },
    [metric.unit, tooltipBg, tooltipBorder, tooltipText],
  );

  return (
    <Suspense
      fallback={
        <Box h="360px" display="flex" alignItems="center" justifyContent="center">
          <Spinner size="lg" />
        </Box>
      }
    >
      <CustomMetricRechartsChart
        metric={metric}
        chartData={chartData}
        gridStroke={gridStroke}
        tooltipRenderer={tooltipRenderer}
        reviewSinceDate={reviewSinceDate}
      />
    </Suspense>
  );
}

function CustomMetricTable({ metric }) {
  const borderColor = useColorModeValue('var(--chakra-colors-gray-200)', 'var(--chakra-colors-gray-700)');
  const headerBg = useColorModeValue('var(--chakra-colors-gray-50)', 'var(--chakra-colors-gray-800)');
  const values = metric.values || [];

  const tdStyle = { padding: '10px 12px', borderBottom: `1px solid ${borderColor}` };

  return (
    <Box overflowX="auto">
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {['Period', 'Value', 'Form', 'Filing Date', 'Source'].map((heading) => (
              <th
                key={heading}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderBottom: `1px solid ${borderColor}`,
                  background: headerBg,
                  fontSize: '0.875rem',
                }}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {values.map((point) => (
            <tr key={`${point.period_end}-${point.accession_number}`}>
              <td style={tdStyle}>
                {formatFiscalLabel(point.period_end)}
              </td>
              <td style={{ ...tdStyle, fontWeight: 600 }}>
                {formatCustomMetricValue(point.value, point.unit || metric.unit)}
              </td>
              <td style={tdStyle}>
                {point.source_url ? (
                  <Box
                    as="a"
                    href={point.source_url}
                    target="_blank"
                    rel="noreferrer"
                    color="blue.500"
                    fontWeight="semibold"
                    display="inline-flex"
                    alignItems="center"
                    gap="1"
                    _hover={{ color: 'blue.600' }}
                  >
                    {point.form || '—'}
                    <LuExternalLink size={16} />
                  </Box>
                ) : (
                  <Text fontWeight="semibold">{point.form || '—'}</Text>
                )}
              </td>
              <td style={tdStyle}>
                {point.filing_date || '—'}
              </td>
              <td style={tdStyle}>
                {point.source_label || point.accession_number || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function buildTableTsv(metric) {
  const values = metric.values || [];
  const headers = ['Date', 'Value', 'Form', 'Filing Date', 'Source'];
  const rows = values.map((point) => {
    const date = point.period_end || '—';
    const value = formatCustomMetricValue(point.value, point.unit || metric.unit);
    const form = point.form || '—';
    const filingDate = point.filing_date || '—';
    const source = point.source_label || point.accession_number || '—';
    return [date, value, form, filingDate, source].join('\t');
  });
  return [headers.join('\t'), ...rows].join('\n');
}

function CustomMetricsPanel({
  customMetrics,
  selectedMetricKey,
  customMetricView,
  onMetricChange,
  onViewChange,
  gridStroke,
  publicMetricsUrl,
  reviewSinceDate,
}) {
  const [copyTableStatus, setCopyTableStatus] = useState('idle');
  const selectedMetric =
    customMetrics.find((metric) => metric.key === selectedMetricKey) || customMetrics[0];

  const chartData = useMemo(() => {
    return buildCustomMetricChartData(selectedMetric?.values, reviewSinceDate);
  }, [reviewSinceDate, selectedMetric]);

  const handleCopyTable = useCallback(async () => {
    if (copyTableStatus === 'copying' || !selectedMetric) return;
    setCopyTableStatus('copying');
    try {
      await navigator.clipboard.writeText(buildTableTsv(selectedMetric));
      setCopyTableStatus('copied');
    } catch (err) {
      console.error('Failed to copy table', err);
      setCopyTableStatus('error');
    }
    setTimeout(() => setCopyTableStatus('idle'), 2000);
  }, [copyTableStatus, selectedMetric]);

  if (!selectedMetric) {
    return (
      <Box textAlign="center" py="10">
        <Text color="gray.500">No company KPIs are available for this company yet.</Text>
      </Box>
    );
  }

  return (
    <>
      <CustomMetricControls
        customMetrics={customMetrics}
        selectedMetricKey={selectedMetric.key}
        customMetricView={customMetricView}
        onMetricChange={onMetricChange}
        onViewChange={onViewChange}
        onCopyTable={handleCopyTable}
        copyTableStatus={copyTableStatus}
      />
      {customMetricView === 'table' ? (
        <CustomMetricTable metric={selectedMetric} />
      ) : (
        <CustomMetricChart
          metric={selectedMetric}
          chartData={chartData}
          gridStroke={gridStroke}
          reviewSinceDate={reviewSinceDate}
        />
      )}
      <Text mt="4" fontSize="sm" color="gray.500">
        Company KPIs are extracted from SEC filing tables and linked back to the source filing.
        {' '}
        {publicMetricsUrl && (
          <Link as={RouterLink} to={publicMetricsUrl} color="blue.600" fontWeight="semibold">
            View public KPI data.
          </Link>
        )}
      </Text>
    </>
  );
}

function FinancialLineChart({
  chartRef,
  filteredChartData,
  legendPayload,
  combinedMetricOrder,
  activeMetrics,
  lineType,
  gridStroke,
  financialPeriod,
}) {
  const tooltipBg = useColorModeValue('white', 'gray.800');
  const tooltipBorder = useColorModeValue('gray.200', 'gray.700');
  const tooltipText = useColorModeValue('gray.800', 'gray.100');

  const tooltipRenderer = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      const visiblePayload = payload.filter(
        (item) => activeMetrics[item.dataKey] !== false && item.value != null,
      );
      if (!visiblePayload.length) return null;

      return (
        <Box
          bg={tooltipBg}
          border="1px solid"
          borderColor={tooltipBorder}
          borderRadius="md"
          boxShadow="lg"
          p="3"
          color={tooltipText}
        >
          <Text fontWeight="semibold" mb="2">
            {financialPeriod === 'quarterly' ? formatQuarterLabel(label) : formatFiscalLabel(label)}
          </Text>
          {visiblePayload.map((item) => {
            const config = METRIC_CONFIG[item.dataKey] || {};
            return (
              <Box
                key={item.dataKey}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap="3"
                mb="1"
              >
                <Box display="flex" alignItems="center" gap="2">
                  <Box
                    w="10px"
                    h="10px"
                    borderRadius="full"
                    bg={config.color || item.color || 'gray'}
                  />
                  <Text fontSize="sm">{config.label || item.name}</Text>
                </Box>
                <Text fontSize="sm" fontWeight="semibold">
                  {formatCurrency(item.value)}
                </Text>
              </Box>
            );
          })}
        </Box>
      );
    },
    [activeMetrics, financialPeriod, tooltipBg, tooltipBorder, tooltipText],
  );

  return (
    <Box ref={chartRef} w="full" position="relative" data-chart-export-root="true">
      <Suspense
        fallback={
          <Box h="420px" display="flex" alignItems="center" justifyContent="center">
            <Spinner size="lg" />
          </Box>
        }
      >
        <FinancialRechartsLineChart
          filteredChartData={filteredChartData}
          legendPayload={legendPayload}
          combinedMetricOrder={combinedMetricOrder}
          activeMetrics={activeMetrics}
          lineType={lineType}
          gridStroke={gridStroke}
          tooltipRenderer={tooltipRenderer}
          tickFormatter={financialPeriod === 'quarterly' ? formatQuarterTick : formatFiscalYear}
          minTickGap={financialPeriod === 'quarterly' ? 28 : 20}
        />
      </Suspense>
      <ChartWatermark />
    </Box>
  );
}

const REVIEW_PRICE_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const REVIEW_PERCENT_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
  signDisplay: 'exceptZero',
});

function formatReviewPrice(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '—';
  }
  return REVIEW_PRICE_FORMATTER.format(numericValue);
}

function formatReviewPercent(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '—';
  }
  return REVIEW_PERCENT_FORMATTER.format(numericValue);
}

function StockPriceHistoryPanel({
  notableDates,
  onRemoveNotableDate,
  stockPriceHistory,
  priceHistoryRange = DEFAULT_STOCK_PRICE_HISTORY_RANGE,
  onPriceHistoryRangeChange = () => {},
  smoothLines = false,
  onSmoothLinesChange = () => {},
  referenceDate = null,
  onUseAsSnapshotDate = null,
}) {
  const summaryBg = useColorModeValue('orange.50', 'orange.950');
  const summaryBorder = useColorModeValue('orange.200', 'orange.800');
  const summaryLabel = useColorModeValue('gray.700', 'gray.200');
  const summaryDetail = useColorModeValue('gray.600', 'gray.400');
  const controlLabelColor = useColorModeValue('gray.600', 'gray.300');
  const lineType = smoothLines ? 'monotone' : 'linear';
  const priceChange = referenceDate
    ? computeStockPriceChangeSinceDate(stockPriceHistory.prices, referenceDate, {
      preferAdjustedClose: true,
    })
    : null;
  let changeColor = summaryLabel;
  if (priceChange) {
    if (priceChange.percentChange > 0) {
      changeColor = 'green.500';
    } else if (priceChange.percentChange < 0) {
      changeColor = 'red.400';
    }
  }

  return (
    <>
      <Box
        display="flex"
        alignItems={{ base: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        gap="3"
        flexWrap="wrap"
        mb="3"
      >
        <Box display="flex" alignItems="center" gap="2">
          <Switch.Root
            id="stock-price-smooth-lines-toggle"
            size="sm"
            checked={smoothLines}
            onCheckedChange={(e) => onSmoothLinesChange(e.checked)}
          >
            <Switch.HiddenInput aria-label="Toggle smooth lines" />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <Switch.Label fontSize="sm" color={controlLabelColor}>
              Smooth lines
            </Switch.Label>
          </Switch.Root>
        </Box>
        <HStack
          justify="flex-end"
          gap="1"
          wrap="wrap"
          aria-label="Stock price history range"
          data-testid="stock-price-history-range"
        >
          {STOCK_PRICE_HISTORY_RANGE_OPTIONS.map((option) => {
            const isSelected = option.value === priceHistoryRange;
            return (
              <Button
                key={option.value}
                type="button"
                size="xs"
                variant={isSelected ? 'solid' : 'outline'}
                colorPalette={isSelected ? 'teal' : 'gray'}
                onClick={() => onPriceHistoryRangeChange(option.value)}
                aria-pressed={isSelected}
              >
                {option.label}
              </Button>
            );
          })}
        </HStack>
      </Box>
      {referenceDate && !stockPriceHistory.loading ? (
        <Box
          mb="4"
          px="4"
          py="3"
          borderWidth="1px"
          borderColor={summaryBorder}
          borderRadius="md"
          bg={summaryBg}
          data-testid="review-price-change"
        >
          {priceChange ? (
            <>
              <Text fontSize="sm" color={summaryLabel}>
                Stock price since {referenceDate}
              </Text>
              <HStack align="baseline" gap="2" mt="1" data-testid="review-price-change-headline">
                <Text fontSize="2xl" fontWeight="bold" color={changeColor} lineHeight="1.2">
                  {formatReviewPercent(priceChange.percentChange)}
                </Text>
                <Text fontSize="md" fontWeight="semibold" color={changeColor}>
                  {formatReviewPrice(priceChange.absoluteChange)}
                </Text>
              </HStack>
              <Text fontSize="sm" color={summaryDetail} mt="1" data-testid="review-price-change-detail">
                {formatReviewPrice(priceChange.baselinePrice)}
                {' '}
                (
                {priceChange.baselineDate}
                )
                {' → '}
                {formatReviewPrice(priceChange.latestPrice)}
                {' '}
                (
                {priceChange.latestDate}
                )
              </Text>
            </>
          ) : (
            <Text fontSize="sm" color={summaryDetail}>
              Stock price change since {referenceDate} is unavailable for the loaded history range.
            </Text>
          )}
        </Box>
      ) : null}
      <StockPriceHistoryChart
        notableDates={notableDates}
        onRemoveNotableDate={onRemoveNotableDate}
        prices={stockPriceHistory.prices}
        loading={stockPriceHistory.loading}
        error={stockPriceHistory.error}
        priceStatus={stockPriceHistory.priceStatus}
        priceMessage={stockPriceHistory.priceMessage}
        preferAdjustedClose
        referenceDate={referenceDate}
        referenceLabel={referenceDate ? 'Since' : null}
        lineType={lineType}
        onUseAsSnapshotDate={onUseAsSnapshotDate}
      />
    </>
  );
}

const CHART_DATASET_QUERY_VALUES = new Set(['financials', 'stockPrice', 'custom']);

function useStockChartsController(ticker) {
  const { data: stockInfo, loading: stockInfoLoading } = useStockInfo(ticker);
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(
    stockChartsReducer,
    undefined,
    getInitialChartState,
  );
  const {
    chartData,
    metrics,
    hiddenMetrics,
    loading,
    error,
    startYear,
    endYear,
    copyStatus,
    smoothLines,
    customMetrics,
    customMetricsLoading,
    customMetricsError,
    selectedDataset,
    financialPeriod,
    financialView,
    selectedCustomMetricKey,
    customMetricView,
    customMetricsBannerDismissed,
    priceHistoryRange,
    stockPriceHistory,
  } = state;
  const chartRef = useRef(null);

  useEffect(() => {
    const dataset = searchParams.get('dataset');
    if (!CHART_DATASET_QUERY_VALUES.has(dataset)) {
      return;
    }
    dispatch({
      type: 'set-selected-dataset',
      selectedDataset: dataset,
    });
  }, [searchParams]);

  const apiBaseUrl = useMemo(() => {
    const base = process.env.REACT_APP_API_BASE_URL || '/api';
    return base.endsWith('/') ? base.slice(0, -1) : base;
  }, []);

  const cardBg = useColorModeValue('white', 'gray.900');
  const cardBorder = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    if (!ticker) {
      dispatch({ type: 'fetch-skipped' });
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      dispatch({ type: 'fetch-start' });
      try {
        const payload = await loadChartData(apiBaseUrl, ticker, controller.signal, financialPeriod);
        if (!isMounted) return;

        dispatch({
          type: 'fetch-success',
          chartData: payload.data || [],
          metrics: payload.metrics?.length ? payload.metrics : ORDERED_METRICS,
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Failed to load chart data', err);
        if (isMounted) {
          dispatch({
            type: 'fetch-error',
            error: 'Unable to load historical charts right now.',
          });
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [apiBaseUrl, financialPeriod, ticker]);

  useEffect(() => {
    dispatch({
      type: 'set-banner-dismissed',
      dismissed: isCustomMetricsBannerDismissed(ticker),
    });
  }, [ticker]);

  useEffect(() => {
    if (!ticker) {
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchCustomMetrics = async () => {
      dispatch({ type: 'custom-fetch-start' });
      try {
        const payload = await loadCustomMetrics(apiBaseUrl, ticker, controller.signal);
        if (!isMounted) return;
        dispatch({
          type: 'custom-fetch-success',
          metrics: payload.metrics || [],
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Failed to load custom metrics', err);
        if (isMounted) {
          dispatch({
            type: 'custom-fetch-error',
            error: 'Unable to load company KPIs right now.',
          });
        }
      }
    };

    fetchCustomMetrics();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [apiBaseUrl, ticker]);

  useEffect(() => {
    if (!ticker) {
      dispatch({ type: 'price-fetch-skipped' });
      return;
    }
    if (stockInfoLoading) return;

    let isMounted = true;
    const controller = new AbortController();
    const historyEnd = stockInfo?.last_trading_date
      ? new Date(`${stockInfo.last_trading_date}T00:00:00Z`)
      : new Date();
    const { startDate, endDate } = buildPriceHistoryRange(priceHistoryRange, historyEnd);

    const fetchPriceHistory = async () => {
      dispatch({ type: 'price-fetch-start', startDate, endDate });
      try {
        const payload = await getStockPriceHistory({
          ticker,
          startDate,
          endDate,
          range: priceHistoryRange === 'max' ? 'max' : undefined,
          signal: controller.signal,
        });
        if (!isMounted) return;

        dispatch({
          type: 'price-fetch-success',
          prices: payload?.prices || [],
          priceStatus: payload?.price_status || null,
          priceMessage: payload?.price_message || null,
          startDate: payload?.start_date || startDate,
          endDate: payload?.end_date || endDate,
        });
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        console.error('Failed to load stock price history', err);
        if (isMounted) {
          dispatch({
            type: 'price-fetch-error',
            error: 'Unable to load stock price history right now.',
          });
        }
      }
    };

    fetchPriceHistory();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [ticker, priceHistoryRange, stockInfoLoading, stockInfo?.last_trading_date]);

  useEffect(() => {
    if (copyStatus !== 'copied' && copyStatus !== 'error') {
      return;
    }

    const resetTimeout = setTimeout(() => {
      dispatch({ type: 'set-copy-status', copyStatus: 'idle' });
    }, 2000);

    return () => {
      clearTimeout(resetTimeout);
    };
  }, [copyStatus]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(SMOOTH_LINES_STORAGE_KEY, smoothLines ? 'true' : 'false');
    } catch (err) {
      console.warn('Unable to persist smooth lines preference', err);
    }
  }, [smoothLines]);

  const combinedMetricOrder = useMemo(() => {
    const known = ORDERED_METRICS.filter((metric) => metrics.includes(metric));
    const extras = metrics.filter((metric) => !known.includes(metric));
    return [...known, ...extras];
  }, [metrics]);

  const activeMetrics = useMemo(() => {
    return metrics.reduce((acc, metric) => {
      acc[metric] = !hiddenMetrics[metric];
      return acc;
    }, {});
  }, [metrics, hiddenMetrics]);

  const legendPayload = useMemo(() => {
    return combinedMetricOrder.reduce((payload, metricKey) => {
      if (activeMetrics[metricKey] === false) {
        return payload;
      }
      const config = METRIC_CONFIG[metricKey] || {};
      payload.push({
        value: config.label || metricKey,
        type: 'line',
        id: metricKey,
        color: config.color || '#8884d8',
        dataKey: metricKey,
      });
      return payload;
    }, []);
  }, [combinedMetricOrder, activeMetrics]);

  const availableYears = useMemo(() => {
    const years = chartData.flatMap((row) => {
      const d = new Date(row.date);
      return Number.isNaN(d.getTime()) ? [] : [d.getFullYear()];
    });
    return [...new Set(years)].toSorted((a, b) => a - b);
  }, [chartData]);

  const effectiveStart = startYear ?? availableYears[0];
  const effectiveEnd = endYear ?? availableYears[availableYears.length - 1];

  const filteredChartData = useMemo(() => {
    if (!availableYears.length) return chartData;
    return chartData.filter((row) => {
      const d = new Date(row.date);
      if (Number.isNaN(d.getTime())) return false;
      const y = d.getFullYear();
      return y >= effectiveStart && y <= effectiveEnd;
    });
  }, [chartData, availableYears, effectiveStart, effectiveEnd]);

  const toggleMetric = useCallback((metricKey) => {
    dispatch({ type: 'toggle-metric', metricKey });
  }, []);

  const handleStartYearChange = useCallback((year) => {
    dispatch({
      type: 'set-year-range',
      startYear: year,
      ...(year > effectiveEnd ? { endYear: year } : {}),
    });
  }, [effectiveEnd]);

  const handleEndYearChange = useCallback((year) => {
    dispatch({
      type: 'set-year-range',
      endYear: year,
      ...(year < effectiveStart ? { startYear: year } : {}),
    });
  }, [effectiveStart]);

  const handleSmoothLinesChange = useCallback((checked) => {
    dispatch({ type: 'set-smooth-lines', smoothLines: checked });
  }, []);

  const handlePriceHistoryRangeChange = useCallback((rangeKey) => {
    dispatch({ type: 'set-price-history-range', rangeKey });
  }, []);

  const handleCopyChart = useCallback(async () => {
    if (!chartRef.current) return;
    if (copyStatus === 'copying') return;

    const clipboardSupported =
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.write === 'function' &&
      typeof window !== 'undefined' &&
      (window.ClipboardItem || typeof ClipboardItem !== 'undefined');

    if (!clipboardSupported) {
      dispatch({ type: 'set-copy-status', copyStatus: 'error' });
      return;
    }

    dispatch({ type: 'set-copy-status', copyStatus: 'copying' });

    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: CHART_EXPORT_BACKGROUND,
        onclone: prepareChartExportClone,
        scale: window.devicePixelRatio || 1,
      });

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((result) => {
          if (result) {
            resolve(result);
          } else {
            reject(new Error('Unable to convert chart to image'));
          }
        }, 'image/png');
      });

      const ClipboardItemCtor =
        (typeof window !== 'undefined' && window.ClipboardItem) ||
        (typeof ClipboardItem !== 'undefined' ? ClipboardItem : null);

      if (!ClipboardItemCtor) {
        throw new Error('ClipboardItem is not supported in this browser');
      }

      await navigator.clipboard.write([new ClipboardItemCtor({ 'image/png': blob })]);
      dispatch({ type: 'set-copy-status', copyStatus: 'copied' });
    } catch (err) {
      console.error('Failed to copy chart', err);
      dispatch({ type: 'set-copy-status', copyStatus: 'error' });
    }
  }, [copyStatus]);

  const triggerLabel = useMemo(() => {
    const active = combinedMetricOrder.filter((m) => activeMetrics[m] !== false);
    if (active.length === 0) return 'No metrics selected';
    if (active.length === combinedMetricOrder.length) return 'All metrics';
    return active.map((m) => (METRIC_CONFIG[m] || {}).label || m).join(', ');
  }, [combinedMetricOrder, activeMetrics]);

  const lineType = smoothLines ? 'monotone' : 'linear';
  const hasCustomMetrics = customMetrics.length > 0;
  const activeDataset = selectedDataset === 'custom' && !hasCustomMetrics ? 'financials' : selectedDataset;
  const publicMetricsUrl = ticker ? `/stocks/${ticker.toLowerCase()}/metrics` : '';

  const handleDatasetChange = useCallback((dataset) => {
    dispatch({
      type: 'set-selected-dataset',
      selectedDataset: dataset,
    });
  }, []);

  const handleFinancialPeriodChange = useCallback((period) => {
    dispatch({
      type: 'set-financial-period',
      financialPeriod: period,
    });
  }, []);

  const handleFinancialViewChange = useCallback((view) => {
    dispatch({
      type: 'set-financial-view',
      financialView: view,
    });
  }, []);

  const handleCustomMetricChange = useCallback((metricKey) => {
    dispatch({
      type: 'set-selected-custom-metric',
      metricKey,
    });
  }, []);

  const handleCustomMetricViewChange = useCallback((view) => {
    dispatch({
      type: 'set-custom-metric-view',
      view,
    });
  }, []);

  const handleShowCustomMetrics = useCallback(() => {
    persistCustomMetricsBannerDismissed(ticker);
    dispatch({ type: 'set-banner-dismissed', dismissed: true });
    dispatch({
      type: 'set-selected-dataset',
      selectedDataset: 'custom',
    });
  }, [ticker]);

  return {
    activeDataset,
    activeMetrics,
    availableYears,
    cardBg,
    cardBorder,
    chartData,
    chartRef,
    combinedMetricOrder,
    copyStatus,
    customMetricView,
    customMetrics,
    customMetricsBannerDismissed,
    customMetricsError,
    customMetricsLoading,
    effectiveEnd,
    effectiveStart,
    error,
    filteredChartData,
    financialPeriod,
    financialView,
    handleCopyChart,
    handleCustomMetricChange,
    handleCustomMetricViewChange,
    handleDatasetChange,
    handleFinancialPeriodChange,
    handleFinancialViewChange,
    handleEndYearChange,
    handlePriceHistoryRangeChange,
    handleShowCustomMetrics,
    handleSmoothLinesChange,
    handleStartYearChange,
    hasCustomMetrics,
    legendPayload,
    lineType,
    loading,
    priceHistoryRange,
    publicMetricsUrl,
    selectedCustomMetricKey,
    smoothLines,
    stockPriceHistory,
    toggleMetric,
    triggerLabel,
  };
}

function StockCharts() {
  const { ticker: rawTicker } = useParams();
  const ticker = rawTicker ? rawTicker.toUpperCase() : '';
  const navigate = useNavigate();
  const { isActive: isReviewModeActive, sinceDate } = useStockReviewMode();
  const {
    isAuthenticated,
    getAccessTokenSilently,
  } = useAuth0();
  const handleUseAsSnapshotDate = useCallback((date) => {
    if (!ticker || !/^\d{4}-\d{2}-\d{2}$/.test(date || '')) {
      return;
    }
    navigate(`/stock/${ticker.toLowerCase()}/snapshot?date=${encodeURIComponent(date)}`);
  }, [navigate, ticker]);
  const gridStroke = useColorModeValue('#E2E8F0', '#2D3748');
  const notableDatesApiBaseUrl = useMemo(() => {
    const raw = process.env.REACT_APP_API_URL || '';
    return raw.endsWith('/') ? raw.slice(0, -1) : raw;
  }, []);
  const { notableDates, deleteNotableDate } = useNotableDates({
    apiBaseUrl: notableDatesApiBaseUrl,
    ticker,
    getAccessTokenSilently,
    isAuthenticated,
  });
  const {
    activeDataset,
    activeMetrics,
    availableYears,
    cardBg,
    cardBorder,
    chartData,
    chartRef,
    combinedMetricOrder,
    copyStatus,
    customMetricView,
    customMetrics,
    customMetricsBannerDismissed,
    customMetricsError,
    customMetricsLoading,
    effectiveEnd,
    effectiveStart,
    error,
    filteredChartData,
    financialPeriod,
    financialView,
    handleCopyChart,
    handleCustomMetricChange,
    handleCustomMetricViewChange,
    handleDatasetChange,
    handleFinancialPeriodChange,
    handleFinancialViewChange,
    handleEndYearChange,
    handlePriceHistoryRangeChange,
    handleShowCustomMetrics,
    handleSmoothLinesChange,
    handleStartYearChange,
    hasCustomMetrics,
    legendPayload,
    lineType,
    loading,
    priceHistoryRange,
    publicMetricsUrl,
    selectedCustomMetricKey,
    smoothLines,
    stockPriceHistory,
    toggleMetric,
    triggerLabel,
  } = useStockChartsController(ticker);

  return (
    <Box maxW="1200px" mx="auto" w="full">
      <Heading size="lg" mb="2">
        {ticker} Financial Charts
      </Heading>
      {hasCustomMetrics && activeDataset !== 'custom' && !customMetricsBannerDismissed ? (
        <Box
          bg="#dbfce6"
          color="#22543D"
          border="1px solid"
          borderColor="#9AE6B4"
          borderRadius="lg"
          px={{ base: 4, md: 5 }}
          py={{ base: 3, md: 4 }}
          mb="6"
          display="flex"
          flexDirection={{ base: 'column', sm: 'row' }}
          alignItems={{ base: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap="3"
        >
          <Text fontWeight="semibold">
            We found Custom Metrics for this stock!
          </Text>
          <Box display="flex" gap="3" alignItems="center" flexWrap="wrap" flexShrink={0}>
            <Link
              as={RouterLink}
              to={publicMetricsUrl}
              color="#22543D"
              fontWeight="semibold"
              textDecoration="underline"
              textUnderlineOffset="3px"
            >
              Public KPI page
            </Link>
            <Button
              size="sm"
              bg="#2F855A"
              color="white"
              _hover={{ bg: '#276749' }}
              onClick={handleShowCustomMetrics}
            >
              Show me →
            </Button>
          </Box>
        </Box>
      ) : hasCustomMetrics ? null : (
        <Text color="gray.500" mb="6">
          Visualize annual 10-K results or quarterly results to spot trends in
          growth, profitability, leverage, and shareholder returns.
        </Text>
      )}

      <DatasetSelector
        selectedDataset={activeDataset}
        hasCustomMetrics={hasCustomMetrics}
        onDatasetChange={handleDatasetChange}
      />
      {customMetricsError && (
        <Alert.Root status="warning" borderRadius="md" mb="4">
          <Alert.Indicator />
          <Alert.Title>{customMetricsError}</Alert.Title>
        </Alert.Root>
      )}

      <Box
        bg={cardBg}
        border="1px solid"
        borderColor={cardBorder}
        borderRadius="xl"
        p={{ base: 4, md: 6 }}
        boxShadow="md"
      >
        {activeDataset === 'stockPrice' ? (
          <StockPriceHistoryPanel
            notableDates={notableDates}
            onRemoveNotableDate={deleteNotableDate}
            stockPriceHistory={stockPriceHistory}
            priceHistoryRange={priceHistoryRange}
            onPriceHistoryRangeChange={handlePriceHistoryRangeChange}
            smoothLines={smoothLines}
            onSmoothLinesChange={handleSmoothLinesChange}
            referenceDate={isReviewModeActive ? sinceDate : null}
            onUseAsSnapshotDate={handleUseAsSnapshotDate}
          />
        ) : activeDataset === 'custom' && customMetricsLoading ? (
          <Box display="flex" alignItems="center" justifyContent="center" py="16">
            <Spinner size="lg" />
          </Box>
        ) : activeDataset === 'custom' ? (
          <CustomMetricsPanel
            customMetrics={customMetrics}
            selectedMetricKey={selectedCustomMetricKey}
            customMetricView={customMetricView}
            onMetricChange={handleCustomMetricChange}
            onViewChange={handleCustomMetricViewChange}
            gridStroke={gridStroke}
            publicMetricsUrl={publicMetricsUrl}
            reviewSinceDate={isReviewModeActive ? sinceDate : null}
          />
        ) : (
          <>
            <FinancialPeriodSelector
              financialPeriod={financialPeriod}
              onFinancialPeriodChange={handleFinancialPeriodChange}
              financialView={financialView}
              onFinancialViewChange={handleFinancialViewChange}
            />
            {loading ? (
              <Box display="flex" alignItems="center" justifyContent="center" py="16">
                <Spinner size="lg" />
              </Box>
            ) : error ? (
              <Alert.Root status="error" borderRadius="md">
                <Alert.Indicator />
                <Alert.Title>{error}</Alert.Title>
              </Alert.Root>
            ) : !chartData.length ? (
              <Box textAlign="center" py="10">
                <Text color="gray.500">
                  {FINANCIAL_PERIOD_COPY[financialPeriod].empty}
                </Text>
              </Box>
            ) : (
              <>
                <ChartControls
                  combinedMetricOrder={combinedMetricOrder}
                  activeMetrics={activeMetrics}
                  triggerLabel={triggerLabel}
                  onToggleMetric={toggleMetric}
                  onCopyChart={handleCopyChart}
                  copyStatus={copyStatus}
                  smoothLines={smoothLines}
                  onSmoothLinesChange={handleSmoothLinesChange}
                  availableYears={availableYears}
                  effectiveStart={effectiveStart}
                  effectiveEnd={effectiveEnd}
                  onStartYearChange={handleStartYearChange}
                  onEndYearChange={handleEndYearChange}
                  financialView={financialView}
                />
                {financialView === 'table' ? (
                  <FinancialResultsTable
                    filteredChartData={filteredChartData}
                    combinedMetricOrder={combinedMetricOrder}
                    activeMetrics={activeMetrics}
                    financialPeriod={financialPeriod}
                  />
                ) : (
                  <FinancialLineChart
                    chartRef={chartRef}
                    filteredChartData={filteredChartData}
                    legendPayload={legendPayload}
                    combinedMetricOrder={combinedMetricOrder}
                    activeMetrics={activeMetrics}
                    lineType={lineType}
                    gridStroke={gridStroke}
                    financialPeriod={financialPeriod}
                  />
                )}
                <Text mt="4" fontSize="sm" color="gray.500" data-testid="financial-period-caption">
                  {FINANCIAL_PERIOD_COPY[financialPeriod].caption}
                </Text>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export default StockCharts;
